/**
 * SEO HEADING STRUCTURE - Online Workspace
 * ========================================
 * 
 * TITLE: Online Workspace | CSW | Online Workspace | Productivity Tool | Study Stream
 * META DESCRIPTION: Free online workspace with customizable backgrounds...
 * 
 * H1 (1 tag):
 *   - "Online Workspace" (hero section)
 * 
 * H2 (3 tags):
 *   - "Everything You Need to Stay Productive" (features section)
 *   - "Why Choose Our Workspace?" (benefits section)
 *   - "Ready to Transform Your Productivity?" (CTA section)
 * 
 * H3 (10 tags):
 *   - Feature card titles (6 cards): "Customize Your Background", "Flexible Modes", etc.
 *   - Benefit titles (4 items): "Zero installation", "Real-time presence", etc.
 */

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Monitor, Users, Cloud, Zap, CheckCircle2, Globe, ArrowRight, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const workspaceAlternatives = [
  { name: "Notion", path: "/online-workspace/csw-vs-notion/" },
  { name: "Monday.com", path: "/online-workspace/csw-vs-monday/" },
  { name: "ClickUp", path: "/online-workspace/csw-vs-clickup/" },
  { name: "Asana", path: "/online-workspace/csw-vs-asana/" },
  { name: "Trello", path: "/online-workspace/csw-vs-trello/" },
  { name: "Slack", path: "/online-workspace/csw-vs-slack/" },
  { name: "Miro", path: "/online-workspace/csw-vs-miro/" },
  { name: "Basecamp", path: "/online-workspace/csw-vs-basecamp/" },
  { name: "Coda", path: "/online-workspace/csw-vs-coda/" },
  { name: "Airtable", path: "/online-workspace/csw-vs-airtable/" },
];

const features = [
  {
    icon: Monitor,
    title: "Customize Your Background",
    description: "Personalize your workspace with custom backgrounds that match your mood and enhance your focus.",
  },
  {
    icon: Users,
    title: "Flexible Modes",
    description: "Choose your mode - study online with people, or create a private space with music and timer.",
  },
  {
    icon: Zap,
    title: "Track Your Productivity",
    description: "Monitor your progress and see how much you've accomplished with real-time productivity insights.",
  },
  {
    icon: CheckCircle2,
    title: "Kanban Task Management",
    description: "Organize your tasks with an intuitive Kanban board. Drag, drop, and track your work.",
  },
  {
    icon: Cloud,
    title: "Calendar Reminders",
    description: "Never miss important deadlines. Set reminders and sync with your calendar.",
  },
  {
    icon: Globe,
    title: "Meet Like-Minded People",
    description: "Connect with others in Chill, Study, or Work modes. Find friends who share your focus.",
  },
];

export default function OnlineWorkspace() {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Online Workspace | CSW | Online Workspace | Productivity Tool | Study Stream"
        description="Free online workspace with customizable backgrounds, Kanban task management, Pomodoro timer, and productivity tracking. Study with focus and stay organized."
        keywords="online workspace, virtual workspace, study room online, productivity workspace, task management, pomodoro timer"
        canonicalUrl="/online-workspace/"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
          
          {/* Animated Glow Effects */}
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-left space-y-8 animate-fade-in">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                  <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" />
                  <span className="text-sm font-medium text-muted-foreground">Work From Anywhere</span>
                </div>
                
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                  <span className="text-gradient">Online Workspace</span>
                </h1>
                
                <p className="text-xl sm:text-2xl text-muted-foreground font-light max-w-xl">
                  A virtual productivity environment designed for focus, collaboration, and achievement.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button 
                    size="lg" 
                    className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Start Working Now
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Try for Free
                    </a>
                  </Button>
                </div>
                
                <div className="flex items-center gap-8 pt-8">
                  <div>
                    <p className="text-3xl font-bold text-gradient">24/7</p>
                    <p className="text-sm text-muted-foreground">Always Open</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">3 Modes</p>
                    <p className="text-sm text-muted-foreground">Chill, Study, Work</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">Free</p>
                    <p className="text-sm text-muted-foreground">Forever</p>
                  </div>
                </div>
              </div>
              
              {/* Right Video */}
              <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
                  <video 
                    src="/videos/productivity-demo.mp4" 
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
                
                {/* Floating Card */}
                <div className="absolute bottom-8 left-8 right-8 bg-card/90 backdrop-blur-md border border-border rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Your Virtual Office</p>
                      <p className="text-xl font-bold text-foreground">Work Anywhere</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <Monitor className="w-6 h-6 text-primary-foreground" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Everything You Need to{" "}
                <span className="text-gradient">Stay Productive</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                A complete digital workspace with all the tools for focused, productive work
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={feature.title}
                    className="group bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-xl"
                  >
                    <CardContent className="p-8 space-y-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      
                      <h3 className="text-xl font-semibold text-foreground font-display">
                        {feature.title}
                      </h3>
                      
                      <p className="text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 px-4 relative">
          <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-[120px]" />
          
          <div className="container mx-auto max-w-4xl relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Why Choose Our{" "}
                <span className="text-gradient">Workspace</span>?
              </h2>
              <p className="text-xl text-muted-foreground">
                Built for modern workers who value flexibility, focus, and connection
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              {[
                { title: "Zero installation", desc: "Works in any browser with no downloads or setup required." },
                { title: "Real-time presence", desc: "See who's working alongside you with live presence indicators." },
                { title: "Distraction-free", desc: "A clean, minimal environment designed to help you focus." },
                { title: "Free forever", desc: "Full access to all features with no limitations or hidden costs." },
              ].map((item, index) => (
                <div 
                  key={index}
                  className="p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:shadow-lg"
                >
                  <h3 className="text-xl font-semibold text-foreground mb-3 font-display">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[150px]" />
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display">
              Ready to Transform Your{" "}
              <span className="text-gradient">Productivity</span>?
            </h2>
            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              Join thousands of professionals, students, and creators who work better together.
            </p>
            <Button 
              size="lg" 
              className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Launch Your Workspace
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
        </section>

        {/* Compare Workspace Tools Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background to-muted" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Compare{" "}
                <span className="text-gradient">Workspace Tools</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                See how CSW compares to other popular productivity platforms
              </p>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {workspaceAlternatives.map((tool) => (
                <Link
                  key={tool.path}
                  to={tool.path}
                  className="group p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-lg text-center"
                >
                  <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                    CSW vs {tool.name}
                  </span>
                  <ExternalLink className="w-3 h-3 inline-block ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}